import { action, makeAutoObservable, observableRef, reaction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '@cutwise/utils';
import { tracker } from '@openreplay/tracker';

import locationReload from '@/common/helpers/locationReload';
import useStore from '@/common/hooks/useStore';
import { HttpStatus } from '@/common/network/constants/httpStatus';
import { MANAGER, securityManager } from '@/common/services/SecurityManager';
import Storage, { KEY_AUTH_TOKEN, KEY_GUEST_ID, KEY_USER } from '@/common/services/Storage';
import AppError from '@/common/state/AppError';
import SurveyAPI from '@/survey/api/SurveyAPI';
import AuthService from '@/user/services/AuthService';

import UserAPI from '../api/UserAPI';
import { User, UserModel } from '../entity/User';
import Token from '../services/token';

const LISTEN_FOR_TOKEN_TIMEOUT = 1000;

export default class UserStore {
  private readonly userAPI: UserAPI;
  private readonly surveyAPI: SurveyAPI;
  private listenForTokenTimeout?: SetTimeout;

  // user undefined only before call of "initUserStore"
  // (after it anon user represented by User object with empty fields)
  user?: User;

  companyUsers: User[] = [];

  isBackendUnavailable = false;

  constructor() {
    makeAutoObservable<UserStore, 'userAPI' | 'listenForTokenTimeout'>(this, {
      user: observableRef,
      userAPI: false,
      listenForTokenTimeout: false,
    });

    this.userAPI = new UserAPI();
    this.surveyAPI = new SurveyAPI();

    reaction(
      () => this.user,
      () => {
        securityManager.setUser(this.user!);
      },
    );
  }

  get isAuthorized() {
    return !!this.user && !this.user.isAnonymous;
  }

  initUserStore = async () => {
    try {
      await this.fetch();
    } catch (e) {
      if ((e as AppError).code === HttpStatus.Unauthorized) {
        AuthService.logoutAfterSessionExpiration();
        this.setAnonUser();
      } else {
        this.setIsBackendUnavailable(true);

        return;
      }
    }

    try {
      if (this.isAuthorized) {
        this.fetchCompanyUsers();
      }

      this.syncUserUuid().catch((e) => logger.error(e));
      this.listenForToken();
    } catch (e) {
      // do nothing
    }
  };

  setUser(user: User) {
    this.user = user;
  }

  getGuestId(): Nullable<string> {
    return Storage.getObject(KEY_GUEST_ID, Storage.STORAGE_LOCAL);
  }

  refreshUserInfo() {
    return this.userAPI.fetchItem().then((res) => {
      Storage.saveObject(KEY_USER, res.content, Storage.STORAGE_LOCAL);
      this.setUser(new User(res.content));

      if (res.content?.email) {
        tracker.identify(res.content.email);
      }
    });
  }

  private fetch = () => {
    if (!Token.accessToken) {
      this.setAnonUser();

      return Promise.resolve(null);
    }

    return this.refreshUserInfo();
  };

  private fetchCompanyUsers = () => {
    if (securityManager.isGranted(MANAGER)) {
      this.userAPI.fetchCompanyUsers().then(
        action((res) => {
          if (!res.content || !Array.isArray(res.content)) {
            return;
          }

          this.companyUsers = res.content.map((companyUser: Partial<UserModel>) => new User(companyUser));
        }),
      ).catch((e) => logger.error(e));
    }
  };

  private listenForToken = () => {
    clearTimeout(this.listenForTokenTimeout);
    this.listenForTokenTimeout = setTimeout(() => {
      /**
       * Do not use Storage here to avoid try..catch and make it spend less of memory
       *
       * Notice about ServiceWorker. We can move this check to Worker, but the problem is - Worker doesn't have access to "localStorage",
       * the only way to make that check is to implement messaging between worker and main flow, which doesn't make this process easier
       * */
      const hasToken = !!localStorage.getItem(KEY_AUTH_TOKEN);

      if ((this.isAuthorized && !hasToken) || (hasToken && !this.isAuthorized)) {
        clearTimeout(this.listenForTokenTimeout);
        locationReload();

        return;
      }

      this.listenForToken();
    }, LISTEN_FOR_TOKEN_TIMEOUT);
  };

  private async syncUserUuid() {
    const guestId = this.getGuestId();

    if (!this.isAuthorized) {
      if (!guestId) {
        // when user opens Cutwise and he has no uuid in LocalStorage - put uuid in storage
        // happens when user open Cutwise for the first time or after storage cleanup
        Storage.saveObject(KEY_GUEST_ID, uuidv4(), Storage.STORAGE_LOCAL);
      }

      return;
    }

    if (!guestId) {
      return;
    }

    await this.surveyAPI.claimGuestResults(guestId);

    Storage.removeObject(KEY_GUEST_ID, Storage.STORAGE_LOCAL);
  }

  private setAnonUser = () => {
    this.setUser(new User());
  };

  private setIsBackendUnavailable = (isBackendUnavailable: boolean) => {
    this.isBackendUnavailable = isBackendUnavailable;
  };
}

export const useUserStore = () => useStore(UserStore);

export const userStore = new UserStore();
